import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Modal } from "react-bootstrap";
import ConfirmationModal from "../../stories/Modals/ConfirmationModal";
import { sessionWarningMessage, VH_FLAG } from "../../utils/constants";
import PropTypes from "prop-types";

const AuthContext = createContext();

// Logout confirmation modal component
const LogoutModal = ({ show, onConfirm, onCancel }) => (
  <Modal show={show} onHide={onCancel} backdrop="static" keyboard={false}>
    <Modal.Header>
      <Modal.Title>Confirm Logout</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p className="mb-1">
        Your session has expired due to inactivity. Please sign in again to
        continue.
      </p>
      <p className="text-muted small mb-0">
        Any unsaved changes will be lost. You will need to sign in again to
        access your account.
      </p>
    </Modal.Body>
    <Modal.Footer>
      <button className="btn btn-primary" onClick={onConfirm}>
        Logout
      </button>
    </Modal.Footer>
  </Modal>
);

LogoutModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem("isLoggedIn") === "true",
  );
  const [role, setRole] = useState("");
  const [clinicalPatientDetails, setClinicalPatientDetails] = useState([]);
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem("access_token") || "",
  );
  const [refreshToken, setRefreshToken] = useState(
    localStorage.getItem("refresh_token") || "",
  );
  const [user, setUser] = useState(localStorage.getItem("user_id"));
  const [showWarning, setShowWarning] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false); // NEW
  const [userInitials, setUserInitials] = useState("guest");
  const [toast, setToast] = useState(null);
  const [featureFlag, setFeatureFlag] = useState(false);
  const [featureFlagLoading, setFeatureFlagLoading] = useState(true);
  const [allowedStage, setAllowedStage] = useState(
    sessionStorage.getItem("allowedStage") || "",
  );
  const [displayUserInfo, setDisplayUserInfo] = useState(null);

  const allowStage = useCallback((stage) => {
    setAllowedStage(stage);
    sessionStorage.setItem("allowedStage", stage);
  }, []);

  const resetAllowedStage = useCallback(() => {
    setAllowedStage("");
    sessionStorage.removeItem("allowedStage");
  }, []);

  const showToast = (header, body, image, toastConfig = {}) => {
    const { icon, className } = toastConfig;
    const Icon = icon;
    setToast({ header, body, imgSrc: image, icon: Icon, className });
  };

  const INACTIVITY_TIME = 2 * 60 * 60 * 1000;
  const WARNING_TIME = 5 * 60 * 1000;

  let warningTimeout;
  let logoutTimeout;

  const resetTimers = useCallback(() => {
    clearTimeout(warningTimeout);
    clearTimeout(logoutTimeout);

    if (isAuthenticated) {
      warningTimeout = setTimeout(() => {
        setShowWarning(true);
      }, INACTIVITY_TIME);

      logoutTimeout = setTimeout(() => {
        setShowWarning(false);
        logout();
      }, INACTIVITY_TIME + WARNING_TIME);
    }
  }, [isAuthenticated]);

  const handleUserActivity = useCallback(() => {
    resetTimers();
    setShowWarning(false);
  }, [resetTimers]);

  useEffect(() => {
    globalThis.addEventListener("mousemove", handleUserActivity);
    globalThis.addEventListener("keydown", handleUserActivity);

    if (isAuthenticated) resetTimers();

    return () => {
      globalThis.removeEventListener("mousemove", handleUserActivity);
      globalThis.removeEventListener("keydown", handleUserActivity);
    };
  }, [isAuthenticated, handleUserActivity, resetTimers]);

  useEffect(() => {
    setUserInitials(localStorage.getItem("username"));
  }, [isAuthenticated]);

  const login = (data) => {
    localStorage.setItem("isLoggedIn", "true");
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
    sessionStorage.setItem("email", data.email);
    localStorage.setItem("username", data.fullname);
    localStorage.setItem("mfa_enabled", data.mfa_enabled || false);
    if (data.id) {
      localStorage.setItem("user_id", data.id);
    }
    setRole(data.role);
    setAccessToken(data.access_token || localStorage.getItem("access_token"));
    setRefreshToken(
      data.refresh_token || localStorage.getItem("refresh_token"),
    );
    if (data.id) {
      setUser(data.id);
    }
    setTimeout(() => {
      setIsAuthenticated(localStorage.getItem("isLoggedIn") === "true");
      resetTimers();
    }, 500);
  };

  // Actual logout — clears all state and storage
  const logout = () => {
    setIsAuthenticated(false);
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    const rememberMe = localStorage.getItem("rememberMe");
    sessionStorage.clear();
    localStorage.clear();
    if (rememberedEmail) {
      localStorage.setItem("rememberedEmail", rememberedEmail);
    }
    if (rememberMe) {
      localStorage.setItem("rememberMe", rememberMe);
    }
    clearTimeout(warningTimeout);
    clearTimeout(logoutTimeout);
  };

  // Opens the logout confirmation modal (use this instead of calling logout directly)
  const openLogoutModal = () => setShowLogoutModal(true);

  // Confirmed via modal → run real logout
  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    logout();
  };

  // Cancelled via modal → just close
  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
    logout();
  };

  const fetchFeatureFlag = async () => {
    try {
      const response = await fetch(VH_FLAG, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setFeatureFlag(data.feature_flag);
    } catch (error) {
      setFeatureFlag(false);
    } finally {
      setFeatureFlagLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatureFlag();
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      setIsAuthenticated(localStorage.getItem("isLoggedIn") === "true");
      setAccessToken(localStorage.getItem("access_token") || "");
      setRefreshToken(localStorage.getItem("refresh_token") || "");
      setUser(localStorage.getItem("user_id"));
      setRole(localStorage.getItem("role"));
      setEmail(sessionStorage.getItem("email"));
    };

    globalThis.addEventListener("storage", handleStorageChange);
    return () => globalThis.removeEventListener("storage", handleStorageChange);
  }, []);

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      login,
      logout,
      openLogoutModal, // expose this for navbar/profile buttons to use
      role,
      user,
      updateState: setUser,
      setTitle,
      title,
      email,
      setEmail,
      accessToken,
      refreshToken,
      setAccessToken,
      setRefreshToken,
      showWarning,
      userInitials,
      toast,
      setToast,
      showToast,
      clinicalPatientDetails,
      setClinicalPatientDetails,
      featureFlag,
      featureFlagLoading,
      allowedStage,
      setAllowedStage,
      allowStage,
      resetAllowedStage,
      displayUserInfo,
      setDisplayUserInfo,
    }),
    [
      isAuthenticated,
      login,
      logout,
      openLogoutModal,
      role,
      user,
      setUser,
      setTitle,
      title,
      email,
      setEmail,
      accessToken,
      refreshToken,
      setAccessToken,
      setRefreshToken,
      showWarning,
      userInitials,
      toast,
      setToast,
      showToast,
      clinicalPatientDetails,
      setClinicalPatientDetails,
      featureFlag,
      featureFlagLoading,
      allowedStage,
      setAllowedStage,
      allowStage,
      resetAllowedStage,
      displayUserInfo,
      setDisplayUserInfo,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}

      {/* Session inactivity warning */}
      <ConfirmationModal
        handleClose={() => setShowWarning(false)}
        handleConfirm={() => setShowWarning(false)}
        onCancelClick={() => setShowWarning(false)}
        heading="Warning!"
        hideButtons={true}
        message={sessionWarningMessage}
        show={showWarning}
      />

      {/* Logout confirmation modal */}
      <LogoutModal
        show={showLogoutModal}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
